import { Dispatch, SetStateAction, useEffect, useState } from "react";
import "./SearchGlobalField.scss";
import Select, {
  ActionMeta,
  InputActionMeta,
  OnChangeValue,
} from "react-select";
import {
  CaretDownIcon,
  Checkbox,
  ContentArea,
  MagnifyingGlassIcon,
} from "/App.Components";
import { DesignTheme } from "/App.GeneratedEnums";
import { createSpreadableBem } from "/Helpers/createBem";
import useDebounce from "/Helpers/Hooks/useDebounce";
import { ReactSelectItem } from "/Helpers/ReactSelectItem";
import { ColorTheme } from "/Models/Models/ColorTheme";
import { ContentAreaProps } from "../../../App.GeneratedTypes";
import SearchGlobalFieldOption from "./SearchGlobalFieldOption";

export interface SearchGlobalFieldProps {
  doSearch: () => void;
  doAutocomplete: (text: string) => void;
  id: string;
  tabIndex?: number;
  searchTerm?: string;
  setSearchTerm: (searchTerm: string) => void;
  autocompleteItems?: ReactSelectItem[];
  setAutocompleteItems: (autocompleteItems: ReactSelectItem[]) => void;
  searchButtonAriaLabel: string;
  searchPlaceholder: string;
  noBottomPadding?: boolean;
  noTopPadding?: boolean;
  searchTips?: ContentAreaProps;
  searchTipsLabel?: string;
  fuzzySearchLabel?: string;
  fuzzySearch?: boolean;
  setFuzzySearch?: Dispatch<SetStateAction<boolean>>;
  theme?: DesignTheme;
}

const SearchGlobalField = ({
  tabIndex,
  autocompleteItems,
  doAutocomplete: onAutocomplete,
  doSearch,
  setSearchTerm,
  searchTerm,
  searchButtonAriaLabel,
  searchPlaceholder,
  noBottomPadding,
  noTopPadding,
  id,
  fuzzySearch,
  fuzzySearchLabel,
  setFuzzySearch,
  searchTips,
  searchTipsLabel,
  theme,
}: SearchGlobalFieldProps) => {
  const [selectedOption, setSelectedOption] = useState<ReactSelectItem>();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [initialized, setInitialized] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [tipsExpanded, setTipsExpanded] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      setInitialized(true);
      return;
    }
    const onResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", onResize);
    setIsMobile(window.innerWidth < 1024);
    setInitialized(true);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const debouncedAutocomplete = useDebounce(
    typeof onAutocomplete !== "undefined"
      ? onAutocomplete
      : (_text: string) => {},
    300
  );

  const emptyOption = () => ({ label: "ban", value: 10 });

  //if user actually selects option
  const onChange = (
    newValue: OnChangeValue<ReactSelectItem, false>,
    actionMeta: ActionMeta<ReactSelectItem>
  ) => {
    switch (actionMeta.action) {
      case "select-option":
        setSearchTerm((newValue as ReactSelectItem).label);
        setSelectedOption(newValue as ReactSelectItem);
        doSearch();
        setIsOpen(false);
        break;
      case "clear":
        setSelectedOption(emptyOption);
        break;
    }
  };

  //when user types
  const onInputChange = (newValue: string, actionMeta: InputActionMeta) => {
    switch (actionMeta.action) {
      case "set-value":
        setSearchTerm(newValue);
        debouncedAutocomplete(newValue);
        break;
      case "input-change":
        setSearchTerm(newValue);
        if (newValue) debouncedAutocomplete(newValue);
        break;
      case "input-blur":
        break;
    }
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    setSelectedOption(emptyOption);
    doSearch();
  };

  const atLeastOneMatch = (): boolean =>
    autocompleteItems && searchTerm
      ? autocompleteItems.some((x) =>
          x.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : false;

  const bem = createSpreadableBem("search-global-field-norad");

  const fontSize =
    theme === DesignTheme.Insight
      ? isMobile
        ? "1rem"
        : "1.25rem"
      : isMobile
        ? "1.25rem"
        : "1.5rem";
  return (
    <form
      onSubmit={handleSubmit}
      {...bem("", {
        "--no-bottom-padding": noBottomPadding,
        "--no-top-padding": noTopPadding,
      })}
    >
      <div>
        <div
          {...bem("__input-container", {
            "--half-round": atLeastOneMatch() && isOpen,
            "--theme-insight": theme === DesignTheme.Insight,
          })}
        >
          {initialized && (
            <>
              <label htmlFor={id}>
                <span {...bem("__sr-label")}>{searchButtonAriaLabel}</span>
              </label>
              <Select
                inputId={id}
                isDisabled={tabIndex === -1}
                options={autocompleteItems}
                value={selectedOption}
                inputValue={searchTerm}
                menuIsOpen={atLeastOneMatch() && isOpen}
                onInputChange={onInputChange}
                onMenuOpen={() => setIsOpen(true)}
                onMenuClose={() => setIsOpen(false)}
                closeMenuOnSelect={false}
                openMenuOnFocus={false}
                openMenuOnClick={false}
                placeholder={initialized ? searchPlaceholder : ""}
                onChange={onChange}
                tabSelectsValue={false}
                controlShouldRenderValue={false}
                styles={{
                  placeholder: (baseStyles) => ({
                    ...baseStyles,
                    color: ColorTheme.textNeutral,
                    fontSize: fontSize,
                    fontFamily: "NoradSans",
                    fontWeight:
                      theme === DesignTheme.Insight
                        ? 400
                        : baseStyles.fontWeight,
                    marginLeft:
                      theme === DesignTheme.Insight
                        ? 0
                        : baseStyles.marginLeft,
                    lineHeight:
                      theme === DesignTheme.Insight
                        ? "1.5"
                        : baseStyles.lineHeight,
                  }),
                  menu: (baseStyles) => ({
                    ...baseStyles,
                    top: isMobile ? "calc(100% + 9px)" : "calc(100% + 7px)",
                    position: "absolute",
                    left: -1,
                    margin: "0px",
                    paddingTop: "24px",
                    paddingBottom: "19px",
                    border:
                      "1px solid " +
                      (theme === DesignTheme.Insight
                        ? ColorTheme.insightDarkBlue18
                        : ColorTheme.borderWeaker),
                    borderRadius: "none",
                    boxSizing: "content-box",
                    boxShadow: "none",
                  }),
                  menuList: (baseStyles) => ({
                    ...baseStyles,
                    padding: "unset",
                  }),
                  input: (baseStyles) => ({
                    ...baseStyles,
                    color:
                      theme === DesignTheme.Insight
                        ? ColorTheme.insightDarkBlue
                        : ColorTheme.darkGreen,
                    zIndex: 1,
                    fontFamily: "NoradSans",
                    fontSize: fontSize,
                    fontWeight: theme === DesignTheme.Insight ? 400 : 500,
                    lineHeight:
                      theme === DesignTheme.Insight ? "1.5" : "1.25rem",
                    marginLeft: theme === DesignTheme.Insight ? 0 : undefined,
                    "&:hover": {
                      cursor: "text",
                    },
                  }),
                  container: (baseStyles) => ({
                    ...baseStyles,
                    width: "100%",
                    padding:
                      theme === DesignTheme.Insight
                        ? "0 0 0 calc(1.25rem - 1px)"
                        : isMobile
                          ? "0 0 0 0.75rem"
                          : isMobile
                            ? "1px 3.5rem 0 0.5rem"
                            : "1px 3.5rem 0 1.5rem",
                    position: "absolute",
                    left: 0,
                  }),
                  valueContainer: (baseStyles) => ({
                    ...baseStyles,
                    padding:
                      theme === DesignTheme.Insight ? "0" : baseStyles.padding,
                  }),
                  control: (baseStyles) => ({
                    ...baseStyles,
                    border: "0px",
                    boxShadow: "none",
                    borderRadius: "3rem",
                  }),
                  option: (baseStyles, _state) => ({
                    ...baseStyles,
                    fontFamily: "NoradSans",
                    fontSize: fontSize,
                    fontWeight: 400,
                    padding:
                      theme === DesignTheme.Insight
                        ? "0"
                        : isMobile
                          ? "2px 32px 3px"
                          : "1px 32px",
                  }),
                }}
                components={{
                  IndicatorSeparator: null,
                  DropdownIndicator: null,
                  Option: SearchGlobalFieldOption,
                }}
              />
            </>
          )}
          <button
            type="submit"
            {...bem("__icon", {
              "--theme-insight": theme === DesignTheme.Insight,
            })}
            tabIndex={tabIndex}
            aria-label={searchButtonAriaLabel}
          >
            <MagnifyingGlassIcon
              size={theme === DesignTheme.Insight ? "20" : "24"}
              thickness="Reg"
            />
          </button>
        </div>
        {(searchTips || fuzzySearchLabel) && (
          <div
            {...bem("__fuzzy-and-tips-expander", {
              "--theme-insight": theme === DesignTheme.Insight,
            })}
          >
            {searchTips && searchTipsLabel && (
              <button
                {...bem("__tips-expander", {
                  "--theme-insight": theme === DesignTheme.Insight,
                })}
                onClick={(e) => {
                  e.preventDefault();
                  setTipsExpanded((old) => !old);
                }}
              >
                <div
                  {...bem("__tips-label", {
                    "--theme-insight": theme === DesignTheme.Insight,
                  })}
                >
                  {searchTipsLabel}
                </div>
                <div
                  {...bem("__icon-container", {
                    "--rotated": tipsExpanded,
                    "--theme-insight": theme === DesignTheme.Insight,
                  })}
                >
                  <CaretDownIcon
                    size="24"
                    thickness="Reg"
                    color={
                      theme === DesignTheme.Insight
                        ? ColorTheme.insightDarkBlue
                        : undefined
                    }
                  />
                </div>
              </button>
            )}
            {fuzzySearchLabel && setFuzzySearch && (
              <Checkbox
                label={fuzzySearchLabel}
                name="fuzzy-search"
                checked={fuzzySearch}
                onClick={() => setFuzzySearch((old) => !old)}
                theme={theme}
              />
            )}
          </div>
        )}
        {searchTips && (
          <div {...bem("__expanded-tips", { "--expanded": tipsExpanded })}>
            <ContentArea
              {...searchTips}
              richText={{
                theme:
                  theme === DesignTheme.Insight
                    ? "searchtips-insight"
                    : "searchtips",
              }}
            />
          </div>
        )}
      </div>
    </form>
  );
};

export default SearchGlobalField;
